import { useEffect, useRef, useState, useCallback } from 'react'
import { SActionBtn, SBack, SLowcodeList, SMessage, STableActions } from 'shj-components'
import config from './config'
import { apiData2Table } from '@/utils/data'
import AddOrEditDrawer from '../modalOrDrawers/addOrEditDrawer'
import {
  deleteSignTemplateDetail,
  deleteTemplateDetailBatch,
  getBudgetTypeEnumNew,
  getSignTemplateDetailList,
  importSignTemplate,
} from '@/services/goods'
import { useParams } from 'react-router-dom'
import dayjs from 'dayjs'
import { Button, message, Upload } from 'antd'
import { fileDownload } from '@/utils/download'
import UrlConfig from '@/api/UrlConfigs'

const TemplateDetail = () => {
  const { id: signTemplateId, name } = useParams() || {}
  const listRef = useRef<any>()
  const [open, setOpen] = useState(false)
  const [isAdd, setIsAdd] = useState(false)
  const [clickInfo, setClickInfo] = useState<any>({})
  const [budgetList, setBudgetList] = useState<any>([])
  const [importLoading, setImportLoading] = useState(false)
  const [exportLoading, setExportLoading] = useState(false)
  const [downloadLoading, setDownloadLoading] = useState(false)
  const [rowIndexMap, setRowIndexMap] = useState<Map<string, number>>(new Map())

  // 获取列表数据
  const fetchList = (params: any) => {
    return new Promise((resolve, reject) => {
      try {
        getSignTemplateDetailList({
          params: {
            pageId: params.current,
            pageSize: params.pageSize,
          },
          data: {
            signTemplateId,
            searchFlag: true,
            quoteType: params.query.quoteType,
          },
        }).then((res: any) => {
          const data = apiData2Table(res)

          // 为每行数据添加索引
          const newMap = new Map()
          res.list.forEach((item, index) => {
            newMap.set(item.id, index)
          })
          setRowIndexMap(newMap)

          resolve(data)
        })
      } catch (error) {
        reject({ success: false })
      }
    })
  }

  // 获取报价类型
  const getBudgetList = async () => {
    // 报价类型
    const budgetRes: any = await getBudgetTypeEnumNew()
    listRef.current?.updateSearchData('budgetList', budgetRes)
    setBudgetList(budgetRes)
  }

  // 批量删除
  const batchDelete = useCallback(({ selectedRowKeys }) => {
    SMessage.confirm({
      title: '提示',
      content: '确定要批量删除选中的配置吗？',
      onOk: () => {
        deleteTemplateDetailBatch(selectedRowKeys).then(() => {
          message.success('删除成功')
          listRef.current?.reload()
        })
      },
    })
  }, [])

  // 添加配置
  const addConfig = () => {
    setOpen(true)
    setIsAdd(true)
    setClickInfo({})
  }

  // 编辑配置
  const editConfig = (record: any) => {
    setOpen(true)
    setIsAdd(false)
    setClickInfo(record)
  }

  // 删除配置
  const deleteConfig = (record: any) => {
    SMessage.confirm({
      title: '提示',
      content: '确定删除该配置吗？',
      onOk: () => {
        deleteSignTemplateDetail(record.id).then(() => {
          message.success('删除成功')
          listRef.current?.reload()
        })
      },
    })
  }

  // 下载模版
  const downloadTemplate = () => {
    setDownloadLoading(true)
    fileDownload(UrlConfig.PlusGoodsWeb.downloadSignTemplate)
      .then(() => setDownloadLoading(false))
      .catch(() => setDownloadLoading(false))
  }

  // 批量导入
  const batchImport = (upload: any) => {
    setImportLoading(true)
    const { file, onSuccess, onError } = upload
    const data = new FormData()
    data.append('file', file)
    data.append('signTemplateId', signTemplateId || '')
    return importSignTemplate(data)
      .then((res: any) => {
        listRef.current?.reload()
        onSuccess && onSuccess()
        message.success(res.message || '导入成功')
      })
      .catch(onError)
      .finally(() => setImportLoading(false))
  }

  // 导出
  const exportTemplate = () => {
    setExportLoading(true)
    fileDownload(UrlConfig.PlusGoodsWeb.exportSignTemplate, {
      method: 'POST',
      data: { signTemplateId },
    })
      .then(() => setExportLoading(false))
      .catch(() => setExportLoading(false))
  }

  const extension = {
    request: { fetchList },
    events: { batchDelete, addConfig, exportTemplate },
    // auth: authMap,
    status: {},
    customRender: {
      // 序号
      numberRender(_text: any, record: Record<string, any>) {
        return <span>{(rowIndexMap.get(record.id) || 0) + 1}</span>
      },
      // 开始时间
      startDateRender(_text: any, record: Record<string, any>) {
        return record.startDate ? dayjs(record.startDate).format('YYYY-MM-DD') : ''
      },
      // 结束时间
      endDateRender(_text: any, record: Record<string, any>) {
        return record.endDate ? dayjs(record.endDate).format('YYYY-MM-DD') : ''
      },
      // 操作
      actionTool(_text: any, record: Record<string, any>) {
        return (
          <STableActions>
            <SActionBtn onClick={() => editConfig(record)}>编辑</SActionBtn>
            <SActionBtn danger onClick={() => deleteConfig(record)}>
              删除
            </SActionBtn>
          </STableActions>
        )
      },
      // 下载导入模版
      downloadRender() {
        return (
          <Button onClick={() => downloadTemplate()} loading={downloadLoading}>
            下载导入模板
          </Button>
        )
      },
      // 批量导入
      importRender() {
        return (
          <Upload listType="text" customRequest={batchImport} itemRender={() => null}>
            <Button loading={importLoading}>批量导入</Button>
          </Upload>
        )
      },
      // 导出
      exportRender() {
        return (
          <Button onClick={() => exportTemplate()} loading={exportLoading}>
            导出
          </Button>
        )
      },
    },
  }

  useEffect(() => {
    getBudgetList()
  }, [])

  return (
    <SBack level={2} title={`${name} 模板配置`}>
      <SLowcodeList searchMode={'group'} config={config} ref={listRef} {...extension} />
      <AddOrEditDrawer
        id={signTemplateId || ''}
        budgetList={budgetList}
        isAdd={isAdd}
        listRef={listRef}
        clickInfo={clickInfo}
        open={open}
        onCancel={() => setOpen(false)}
      />
    </SBack>
  )
}

export default TemplateDetail
